import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { useProgressiveList } from '@/hooks/useProgressiveList';
import ProgressiveFooter from '@/components/feature/marketplace/ProgressiveFooter';
import {
  fetchMarketplaceAdminAnnonces,
  fetchMarketplaceVendors,
  setAnnonceBoost,
  setVendorFeatured,
  setAnnoncePubliee,
  setVendorStatus,
  setVendorValidation,
  notifyVendor,
  isProductType,
  isServiceType,
  type MarketplaceAnnonce,
  type MarketplaceVendorRow,
} from '@/lib/marketplaceCatalog';
import CategoryManager from './components/CategoryManager';
import ZifekNetworkCard from '@/components/feature/marketplace/ZifekNetworkCard';
import AiChatStatsCard from '@/components/feature/marketplace/AiChatStatsCard';
import VendorRatingBadge from '@/components/feature/marketplace/VendorRatingBadge';
import { fetchVendorRatings, type VendorRating } from '@/lib/vendorReviews';
import { fetchMarketplaceSettings, setMarketplaceAutoApprove } from '@/lib/marketplaceSettings';

// ─────────────────────────────────────────────────────────────
// Dashboard « Marketplace » — modération & mise en avant.
// Le marchand propriétaire (type de compte « Marketplace mixte ») :
//   • approuve / refuse les demandes de vente de ses vendeurs
//   • met en avant les annonces et les vendeurs de sa place de marché
//   • modère (masque / réaffiche) annonces et vendeurs
// ─────────────────────────────────────────────────────────────

type Tab = 'categories' | 'annonces' | 'demandes' | 'vendeurs' | 'classement';

function priceLabel(prix: string | null): string {
  const n = Number(prix);
  if (!prix || Number.isNaN(n) || n <= 0) return 'Prix à discuter';
  return n.toLocaleString();
}

function annonceTypeMeta(type: string | null): { label: string; icon: string; className: string } {
  if (isProductType(type)) {
    return { label: 'Produit', icon: 'ri-shopping-bag-3-line', className: 'bg-primary-100 text-primary-700' };
  }
  if (isServiceType(type)) {
    return { label: 'Service', icon: 'ri-service-line', className: 'bg-secondary-100 text-secondary-900' };
  }
  return { label: (type || 'Annonce').trim(), icon: 'ri-megaphone-line', className: 'bg-accent-100 text-accent-900' };
}

function validationMeta(validation: string): { label: string; icon: string; className: string } {
  if (validation === 'approuve') {
    return { label: 'Approuvé', icon: 'ri-checkbox-circle-line', className: 'bg-accent-100 text-accent-700' };
  }
  if (validation === 'refuse') {
    return { label: 'Refusé', icon: 'ri-close-circle-line', className: 'bg-red-50 text-red-600' };
  }
  return { label: 'En attente', icon: 'ri-time-line', className: 'bg-secondary-100 text-secondary-900' };
}

function StatCard({ icon, label, value, tone }: { icon: string; label: string; value: number; tone: string }) {
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${tone}`}>
        <i className={`${icon} text-lg`}></i>
      </div>
      <div>
        <div className="text-xl font-bold text-foreground-950 tabular-nums">{value}</div>
        <div className="text-[11px] text-foreground-500 uppercase tracking-wide">{label}</div>
      </div>
    </div>
  );
}

export default function MarketplaceAdminPage() {
  const { user } = useAuth();
  const merchantId = user ? Number(user.id) : null;

  const [tab, setTab] = useState<Tab>('categories');
  const [annonces, setAnnonces] = useState<MarketplaceAnnonce[]>([]);
  const [vendors, setVendors] = useState<MarketplaceVendorRow[]>([]);
  const [ratings, setRatings] = useState<Record<number, VendorRating>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  // Réglages de la marketplace : approbation automatique des vendeurs.
  const [autoApprove, setAutoApprove] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    if (!merchantId) return;
    let active = true;
    fetchMarketplaceSettings(merchantId)
      .then((s) => { if (active) setAutoApprove(s.autoApproveVendors); })
      .catch(() => {});
    return () => { active = false; };
  }, [merchantId]);

  const toggleAutoApprove = async () => {
    if (!merchantId) return;
    const next = !autoApprove;
    setAutoApprove(next);
    setSavingSettings(true);
    try {
      await setMarketplaceAutoApprove(merchantId, next);
    } catch {
      setAutoApprove(!next);
      setError("Impossible d'enregistrer le réglage.");
    } finally {
      setSavingSettings(false);
    }
  };

  const load = useCallback(async () => {
    if (!merchantId) return;
    setLoading(true);
    setError('');
    try {
      const [a, v] = await Promise.all([
        fetchMarketplaceAdminAnnonces(merchantId),
        fetchMarketplaceVendors(merchantId),
      ]);
      setAnnonces(a);
      setVendors(v);
      // Notes moyennes des vendeurs (classement des meilleurs commerces).
      setRatings(await fetchVendorRatings(v.map((x) => x.userId)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    document.title = 'Marketplace — Gestion des vendeurs';
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pendingVendors = useMemo(() => vendors.filter((v) => v.validation === 'en_attente'), [vendors]);
  const boostedCount = annonces.filter((a) => a.is_boost).length;
  const featuredCount = vendors.filter((v) => v.misEnAvant).length;
  const hiddenAnnonces = annonces.filter((a) => Number(a.publique) !== 1).length;
  const suspendedVendors = vendors.filter((v) => Number(v.statut) === 0).length;

  const filteredAnnonces = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return annonces;
    return annonces.filter(
      (a) =>
        (a.titre || '').toLowerCase().includes(q) ||
        (a.owner || '').toLowerCase().includes(q) ||
        (a.ville || '').toLowerCase().includes(q),
    );
  }, [annonces, query]);

  const filteredVendors = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return vendors;
    return vendors.filter(
      (v) =>
        (v.nomcommerce || '').toLowerCase().includes(q) ||
        (v.ville || '').toLowerCase().includes(q),
    );
  }, [vendors, query]);

  const filteredPending = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pendingVendors;
    return pendingVendors.filter(
      (v) =>
        (v.nomcommerce || '').toLowerCase().includes(q) ||
        (v.ville || '').toLowerCase().includes(q),
    );
  }, [pendingVendors, query]);

  // Classement des meilleurs commerces/vendeurs : note moyenne puis volume d'avis.
  const ranking = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? vendors.filter(
          (v) =>
            (v.nomcommerce || '').toLowerCase().includes(q) ||
            (v.ville || '').toLowerCase().includes(q),
        )
      : vendors;
    return base
      .map((v) => ({ vendor: v, rating: ratings[v.userId] || { average: 0, count: 0 } }))
      .sort(
        (x, y) =>
          y.rating.average - x.rating.average ||
          y.rating.count - x.rating.count ||
          x.vendor.nomcommerce.localeCompare(y.vendor.nomcommerce),
      );
  }, [vendors, ratings, query]);

  const annoncePager = useProgressiveList(filteredAnnonces, 12);
  const vendorPager = useProgressiveList(filteredVendors, 12);
  const pendingPager = useProgressiveList(filteredPending, 12);

  const toggleAnnonce = async (a: MarketplaceAnnonce) => {
    const next = !a.is_boost;
    setBusy(`a${a.id}`);
    setAnnonces((prev) => prev.map((x) => (x.id === a.id ? { ...x, is_boost: next } : x)));
    try {
      await setAnnonceBoost(a.id, next);
    } catch {
      setAnnonces((prev) => prev.map((x) => (x.id === a.id ? { ...x, is_boost: a.is_boost } : x)));
      setError("Impossible de mettre à jour l'annonce.");
    } finally {
      setBusy(null);
    }
  };

  const toggleVendor = async (v: MarketplaceVendorRow) => {
    const next = !v.misEnAvant;
    setBusy(`v${v.relationId}`);
    setVendors((prev) => prev.map((x) => (x.relationId === v.relationId ? { ...x, misEnAvant: next } : x)));
    try {
      await setVendorFeatured(v.relationId, next);
    } catch {
      setVendors((prev) => prev.map((x) => (x.relationId === v.relationId ? { ...x, misEnAvant: v.misEnAvant } : x)));
      setError('Impossible de mettre à jour le vendeur.');
    } finally {
      setBusy(null);
    }
  };

  // Modération : publier / dépublier une annonce d'un vendeur.
  const togglePublication = async (a: MarketplaceAnnonce) => {
    const next = Number(a.publique) === 1 ? 0 : 1;
    setBusy(`p${a.id}`);
    setAnnonces((prev) => prev.map((x) => (x.id === a.id ? { ...x, publique: next } : x)));
    try {
      await setAnnoncePubliee(a.id, next === 1);
      if (next === 0 && a.idvendeur) {
        await notifyVendor(a.idvendeur, `Votre publication « ${a.titre} » a été désactivée par la marketplace.`, {
          senderId: merchantId ?? undefined,
          publicationId: a.id,
        });
      }
    } catch {
      setAnnonces((prev) => prev.map((x) => (x.id === a.id ? { ...x, publique: a.publique } : x)));
      setError('Impossible de mettre à jour la publication.');
    } finally {
      setBusy(null);
    }
  };

  // Modération : masquer / réactiver un vendeur.
  const toggleVendorStatus = async (v: MarketplaceVendorRow) => {
    const suspended = Number(v.statut) === 0;
    const next = suspended ? 1 : 0;
    setBusy(`s${v.relationId}`);
    setVendors((prev) => prev.map((x) => (x.relationId === v.relationId ? { ...x, statut: next } : x)));
    try {
      await setVendorStatus(v.relationId, next);
      if (next === 0) {
        await notifyVendor(v.userId, 'Votre compte vendeur a été temporairement masqué de la marketplace.', {
          senderId: merchantId ?? undefined,
        });
      }
    } catch {
      setVendors((prev) => prev.map((x) => (x.relationId === v.relationId ? { ...x, statut: v.statut } : x)));
      setError('Impossible de mettre à jour le vendeur.');
    } finally {
      setBusy(null);
    }
  };

  // Approbation : la demande de vente est acceptée, le vendeur devient visible.
  const approveVendor = async (v: MarketplaceVendorRow) => {
    setBusy(`g${v.relationId}`);
    setVendors((prev) =>
      prev.map((x) => (x.relationId === v.relationId ? { ...x, validation: 'approuve', statut: 1 } : x)),
    );
    try {
      await setVendorValidation(v.relationId, 'approuve');
      await notifyVendor(
        v.userId,
        'Félicitations ! Votre demande de vente sur la marketplace a été approuvée. Vous pouvez désormais publier vos produits et services.',
        { senderId: merchantId ?? undefined },
      );
    } catch {
      setVendors((prev) => prev.map((x) => (x.relationId === v.relationId ? { ...x, validation: v.validation } : x)));
      setError("Impossible d'approuver cette demande.");
    } finally {
      setBusy(null);
    }
  };

  // Refus : la demande n'est pas acceptée et le vendeur reçoit un avertissement.
  const rejectVendor = async (v: MarketplaceVendorRow) => {
    setBusy(`r${v.relationId}`);
    setVendors((prev) => prev.map((x) => (x.relationId === v.relationId ? { ...x, validation: 'refuse' } : x)));
    try {
      await setVendorValidation(v.relationId, 'refuse');
      await notifyVendor(
        v.userId,
        'Avertissement : votre demande de vente sur la marketplace n\u2019a pas été acceptée pour le moment. Vérifiez vos informations et le respect des règles de la marketplace.',
        { senderId: merchantId ?? undefined, type: 'system' },
      );
    } catch {
      setVendors((prev) => prev.map((x) => (x.relationId === v.relationId ? { ...x, validation: v.validation } : x)));
      setError('Impossible de refuser cette demande.');
    } finally {
      setBusy(null);
    }
  };

  const tabButton = (key: Tab, icon: string, label: string, badge?: number) => (
    <button
      type="button"
      onClick={() => { setTab(key); setQuery(''); }}
      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
        tab === key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
      }`}
    >
      <i className={icon}></i>
      {label}
      {badge != null && badge > 0 && (
        <span className="ml-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-[10px] font-bold leading-none">
          {badge}
        </span>
      )}
    </button>
  );

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8 max-w-7xl mx-auto">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary-500 flex items-center justify-center flex-shrink-0">
            <i className="ri-store-3-line text-xl text-background-50"></i>
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Marketplace</h1>
            <p className="text-sm text-foreground-500">Validez vos vendeurs et mettez en avant leurs contenus.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap w-fit"
        >
          <i className="ri-refresh-line"></i>
          Actualiser
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <StatCard icon="ri-user-add-line" label="Demandes en attente" value={pendingVendors.length} tone="bg-accent-100 text-accent-700" />
        <StatCard icon="ri-megaphone-line" label="Annonces" value={annonces.length} tone="bg-primary-100 text-primary-700" />
        <StatCard icon="ri-store-2-line" label="Vendeurs" value={vendors.length} tone="bg-secondary-100 text-secondary-900" />
        <StatCard icon="ri-star-line" label="Vendeurs en avant" value={featuredCount} tone="bg-accent-100 text-accent-700" />
      </div>

      {/* Réglages : approbation des vendeurs */}
      <div className="mb-6 bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-secondary-100 text-secondary-900 flex items-center justify-center flex-shrink-0">
            <i className="ri-user-follow-line text-lg"></i>
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-foreground-900">Approbation des vendeurs</h3>
            <p className="text-xs text-foreground-500 max-w-xl mt-0.5">
              {autoApprove
                ? 'Activée : les nouveaux vendeurs sont approuvés automatiquement dès leur inscription.'
                : 'Désactivée : les vendeurs restent « en attente » jusqu\u2019à votre validation manuelle.'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={toggleAutoApprove}
          disabled={savingSettings}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer disabled:opacity-50 w-fit flex-shrink-0 ${
            autoApprove
              ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
              : 'bg-background-100 text-foreground-700 hover:bg-background-200/70'
          }`}
        >
          <i className={savingSettings ? 'ri-loader-4-line animate-spin' : autoApprove ? 'ri-toggle-fill' : 'ri-toggle-line'}></i>
          {autoApprove ? 'Automatique' : 'Manuelle'}
        </button>
      </div>

      {/* Réseau Zifek Commerce */}
      <div className="mb-6">
        <ZifekNetworkCard role="marketplace" />
      </div>

      {/* Assistant IA : activité générée par les recherches des clients */}
      <div className="mb-6">
        <AiChatStatsCard
          merchantIds={vendors.map((v) => v.userId)}
          title="Assistant IA de la marketplace"
          showRecent
        />
      </div>

      {/* Onglets + recherche */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
          {tabButton('categories', 'ri-price-tag-3-line', 'Catégories')}
          {tabButton('annonces', 'ri-megaphone-line', 'Annonces')}
          {tabButton('demandes', 'ri-user-add-line', 'Demandes', pendingVendors.length)}
          {tabButton('vendeurs', 'ri-store-2-line', 'Vendeurs')}
          {tabButton('classement', 'ri-trophy-line', 'Classement')}
        </div>

        <div className="relative w-full sm:w-72">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={tab === 'categories' ? 'Rechercher une catégorie…' : tab === 'annonces' ? 'Rechercher une annonce…' : tab === 'demandes' ? 'Rechercher une demande…' : 'Rechercher un vendeur…'}
            className="w-full h-11 pl-9 pr-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
          />
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-100 text-xs text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {(hiddenAnnonces > 0 || suspendedVendors > 0) && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-xs text-foreground-600 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="flex items-center gap-1.5">
            <i className="ri-eye-off-line text-foreground-400"></i>
            {hiddenAnnonces} annonce{hiddenAnnonces > 1 ? 's' : ''} masquée{hiddenAnnonces > 1 ? 's' : ''}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-user-forbid-line text-foreground-400"></i>
            {suspendedVendors} vendeur{suspendedVendors > 1 ? 's' : ''} masqué{suspendedVendors > 1 ? 's' : ''}
          </span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : tab === 'categories' ? (
        merchantId ? <CategoryManager commerceId={merchantId} /> : null
      ) : tab === 'annonces' ? (
        annoncePager.total === 0 ? (
          <EmptyBlock icon="ri-megaphone-line" text="Aucune annonce publiée par vos vendeurs pour le moment." />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {annoncePager.visible.map((a) => {
                const img = resolveMediaUrl(a.image);
                const meta = annonceTypeMeta(a.type);
                const on = a.is_boost;
                return (
                  <div key={a.id} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden flex">
                    <div className="relative w-28 h-full min-h-[112px] bg-background-100 flex-shrink-0">
                      {img ? (
                        <img src={img} alt={a.titre} className="absolute inset-0 w-full h-full object-cover object-top" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <i className="ri-image-line text-2xl text-foreground-300"></i>
                        </div>
                      )}
                      <div className="absolute top-1.5 left-1.5 flex flex-col items-start gap-1">
                        {Number(a.publique) !== 1 && (
                          <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-foreground-900/80 text-background-50 whitespace-nowrap">
                            Masquée
                          </span>
                        )}
                        {on && (
                          <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-accent-500 text-background-50 whitespace-nowrap">
                            À la une
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 p-3.5 flex flex-col">
                      <span className={`w-fit px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize flex items-center gap-1 ${meta.className}`}>
                        <i className={meta.icon}></i>
                        {meta.label}
                      </span>
                      <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 mt-1.5">{a.titre}</h3>
                      <p className="text-[11px] text-foreground-400 truncate">
                        {[a.owner, a.ville].filter(Boolean).join(' · ') || '—'}
                      </p>
                      <div className="mt-auto pt-2 flex items-center justify-between gap-2">
                        <span className="text-sm font-bold text-primary-600">{priceLabel(a.prix)}</span>
                        <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => togglePublication(a)}
                          disabled={busy === `p${a.id}`}
                          title={Number(a.publique) === 1 ? 'Désactiver cette publication' : 'Réactiver cette publication'}
                          className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer disabled:opacity-50"
                        >
                          <i className={busy === `p${a.id}` ? 'ri-loader-4-line animate-spin' : Number(a.publique) === 1 ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleAnnonce(a)}
                          disabled={busy === `a${a.id}`}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 ${
                            on
                              ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                              : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                          }`}
                        >
                          <i className={busy === `a${a.id}` ? 'ri-loader-4-line animate-spin' : on ? 'ri-flashlight-fill' : 'ri-flashlight-line'}></i>
                          {on ? 'À la une' : 'Mettre en avant'}
                        </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <ProgressiveFooter
              hasMore={annoncePager.hasMore}
              shown={annoncePager.visible.length}
              total={annoncePager.total}
              remaining={annoncePager.remaining}
              loadMore={annoncePager.loadMore}
              sentinelRef={annoncePager.sentinelRef}
              label="annonces"
            />
          </>
        )
      ) : tab === 'demandes' ? (
        pendingPager.total === 0 ? (
          <EmptyBlock icon="ri-user-add-line" text="Aucune demande de vente en attente. Vous êtes à jour." />
        ) : (
          <>
            <div className="mb-4 px-4 py-2.5 rounded-lg bg-accent-100/70 border border-accent-200 text-xs text-accent-900 flex items-center gap-2">
              <i className="ri-information-line"></i>
              Ces vendeurs souhaitent vendre sur votre marketplace. Approuvez-les pour les rendre visibles, ou refusez en leur envoyant un avertissement.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {pendingPager.visible.map((v) => (
                <VendorCard
                  key={v.relationId}
                  vendor={v}
                  busy={busy}
                  onApprove={approveVendor}
                  onReject={rejectVendor}
                />
              ))}
            </div>
            <ProgressiveFooter
              hasMore={pendingPager.hasMore}
              shown={pendingPager.visible.length}
              total={pendingPager.total}
              remaining={pendingPager.remaining}
              loadMore={pendingPager.loadMore}
              sentinelRef={pendingPager.sentinelRef}
              label="demandes"
            />
          </>
        )
      ) : tab === 'classement' ? (
        ranking.length === 0 ? (
          <EmptyBlock icon="ri-trophy-line" text="Aucun vendeur à classer pour le moment." />
        ) : (
          <>
            <div className="mb-4 px-4 py-2.5 rounded-lg bg-accent-100/70 border border-accent-200 text-xs text-accent-900 flex items-center gap-2">
              <i className="ri-information-line"></i>
              Classement par note moyenne, puis par volume d&apos;avis. Il reflète les avis laissés par les clients (notamment depuis le chat IA).
            </div>
            <div className="space-y-2">
              {ranking.map((row, i) => {
                const medal =
                  i === 0
                    ? 'bg-accent-500 text-background-50'
                    : i === 1
                      ? 'bg-secondary-500 text-background-50'
                      : i === 2
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600';
                const img = resolveMediaUrl(row.vendor.image);
                return (
                  <div
                    key={row.vendor.relationId}
                    className="flex items-center gap-3 bg-background-50 border border-background-200/70 rounded-lg p-3"
                  >
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${medal}`}>
                      {i + 1}
                    </span>
                    <div className="w-11 h-11 rounded-full bg-background-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                      {img ? (
                        <img src={img} alt={row.vendor.nomcommerce} className="w-full h-full object-cover" />
                      ) : (
                        <span className="font-bold text-foreground-400">{row.vendor.nomcommerce.charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-foreground-900 truncate">{row.vendor.nomcommerce}</h3>
                      <span className="text-[11px] text-foreground-400 flex items-center gap-1">
                        <i className="ri-map-pin-line"></i>
                        {[row.vendor.ville, row.vendor.pays].filter(Boolean).join(', ') || 'Ville non précisée'}
                      </span>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <VendorRatingBadge average={row.rating.average} count={row.rating.count} size="md" />
                      <div className="text-[10px] text-foreground-400 mt-0.5">
                        {row.vendor.annonceCount} annonce{row.vendor.annonceCount > 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )
      ) : vendorPager.total === 0 ? (
        <EmptyBlock icon="ri-store-2-line" text="Aucun vendeur rattaché à votre marketplace pour le moment." />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {vendorPager.visible.map((v) => (
              <VendorCard
                key={v.relationId}
                vendor={v}
                busy={busy}
                mode="manage"
                onFeatured={toggleVendor}
                onStatus={toggleVendorStatus}
                onApprove={approveVendor}
              />
            ))}
          </div>
          <ProgressiveFooter
            hasMore={vendorPager.hasMore}
            shown={vendorPager.visible.length}
            total={vendorPager.total}
            remaining={vendorPager.remaining}
            loadMore={vendorPager.loadMore}
            sentinelRef={vendorPager.sentinelRef}
            label="vendeurs"
          />
        </>
      )}

      <p className="mt-8 text-xs text-foreground-400 flex items-start gap-2">
        <i className="ri-information-line mt-0.5"></i>
        <span>
          Les annonces « à la une » remontent en tête des listes publiques. Les vendeurs mis en avant
          apparaissent dans le bloc « Vendeurs mis en avant » de l&apos;accueil.{' '}
          <Link to="/dashboard/banners" className="text-primary-600 hover:text-primary-700 no-underline">
            Gérez vos bannières publicitaires ici
          </Link>
          .
        </span>
      </p>
    </div>
  );
}

function VendorCard({
  vendor: v,
  busy,
  mode = 'request',
  onApprove,
  onReject,
  onFeatured,
  onStatus,
}: {
  vendor: MarketplaceVendorRow;
  busy: string | null;
  mode?: 'request' | 'manage';
  onApprove?: (v: MarketplaceVendorRow) => void;
  onReject?: (v: MarketplaceVendorRow) => void;
  onFeatured?: (v: MarketplaceVendorRow) => void;
  onStatus?: (v: MarketplaceVendorRow) => void;
}) {
  const img = resolveMediaUrl(v.image);
  const on = v.misEnAvant;
  const vMeta = validationMeta(v.validation);
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-12 h-12 rounded-full bg-background-100 overflow-hidden flex items-center justify-center flex-shrink-0">
          {img ? (
            <img src={img} alt={v.nomcommerce} className="w-full h-full object-cover" />
          ) : (
            <span className="text-lg font-bold text-foreground-400">{v.nomcommerce.charAt(0).toUpperCase()}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-foreground-900 truncate">{v.nomcommerce}</h3>
          <span className="text-[11px] text-foreground-400 flex items-center gap-1">
            <i className="ri-map-pin-line"></i>
            {[v.ville, v.pays].filter(Boolean).join(', ') || 'Ville non précisée'}
          </span>
        </div>
        {on && (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-accent-500 text-background-50 flex-shrink-0">
            <i className="ri-star-fill"></i>
            En avant
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${vMeta.className}`}>
          <i className={vMeta.icon}></i>
          {vMeta.label}
        </span>
        {Number(v.statut) === 0 && (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-foreground-900/80 text-background-50">
            <i className="ri-eye-off-line"></i>
            Masqué
          </span>
        )}
        <span className="text-[11px] text-foreground-400 ml-auto">
          {v.annonceCount} annonce{v.annonceCount > 1 ? 's' : ''}
        </span>
      </div>

      {v.createdAt && (
        <p className="text-[11px] text-foreground-400 mb-3 flex items-center gap-1">
          <i className="ri-calendar-line"></i>
          Demandé le {new Date(v.createdAt).toLocaleDateString('fr-FR')}
        </p>
      )}

      {mode === 'request' ? (
        <div className="flex items-center gap-2 pt-3 border-t border-background-200/70">
          <button
            type="button"
            onClick={() => onApprove?.(v)}
            disabled={busy === `g${v.relationId}`}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-[11px] font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            <i className={busy === `g${v.relationId}` ? 'ri-loader-4-line animate-spin' : 'ri-check-line'}></i>
            Approuver
          </button>
          <button
            type="button"
            onClick={() => onReject?.(v)}
            disabled={busy === `r${v.relationId}`}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-[11px] font-semibold bg-background-100 text-foreground-700 hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            <i className={busy === `r${v.relationId}` ? 'ri-loader-4-line animate-spin' : 'ri-close-line'}></i>
            Refuser
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-end gap-1.5 pt-3 border-t border-background-200/70">
          {v.validation === 'refuse' && (
            <button
              type="button"
              onClick={() => onApprove?.(v)}
              disabled={busy === `g${v.relationId}`}
              title="Approuver ce vendeur"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              <i className={busy === `g${v.relationId}` ? 'ri-loader-4-line animate-spin' : 'ri-check-line'}></i>
              Approuver
            </button>
          )}
          <button
            type="button"
            onClick={() => onStatus?.(v)}
            disabled={busy === `s${v.relationId}`}
            title={Number(v.statut) === 0 ? 'Réactiver ce vendeur' : 'Masquer ce vendeur'}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer disabled:opacity-50"
          >
            <i className={busy === `s${v.relationId}` ? 'ri-loader-4-line animate-spin' : Number(v.statut) === 0 ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
          </button>
          <button
            type="button"
            onClick={() => onFeatured?.(v)}
            disabled={busy === `v${v.relationId}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 ${
              on
                ? 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                : 'bg-accent-500 text-background-50 hover:bg-accent-600'
            }`}
          >
            <i className={busy === `v${v.relationId}` ? 'ri-loader-4-line animate-spin' : on ? 'ri-star-off-line' : 'ri-star-line'}></i>
            {on ? 'Retirer' : 'Mettre en avant'}
          </button>
        </div>
      )}
    </div>
  );
}

function EmptyBlock({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
        <i className={`${icon} text-2xl text-foreground-400`}></i>
      </div>
      <p className="text-sm text-foreground-500 max-w-sm">{text}</p>
    </div>
  );
}